import { useCallback, useEffect, useState } from "react";
import "./Withdrawal.css";
import WithdrawalCreditCardForm from "./WithdrawalCreditCardForm";
import WithdrawalDigitalWalletForm from "./WithdrawalDigitalWalletForm";
import AmountForm from "./AmountForm";
import ContactForm from "./ContactForm";
import { PAYMENT_OPTION_INTENT } from "./constants";
import { formatDepositHistoryMessage } from "./depositHistory";
import PaymentResultModal from "./PaymentResultModal";
import { usePaymentStopwatch } from "./usePaymentStopwatch";

/* global PGDataType */

const getPaymentOptionClassName = (paymentOption) => {
  const toSlug = (value) =>
    `${value || "unknown"}`
      .replace(/([a-z])([A-Z])/g, "$1-$2")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");

  return [
    "payment-option-row",
    `payment-option-type-${toSlug(paymentOption.dataType)}`,
    `payment-option-name-${toSlug(paymentOption.displayName)}`
  ].join(" ");
};

function Withdrawal() {
  const sdk = window.__PGWebSdk;
  const {
    start: startPaymentStopwatch,
    finish: finishPaymentAttempt,
    close: closePaymentResult,
    paymentResult
  } = usePaymentStopwatch();

  const [paymentOptions, setPaymentOptions] = useState([]);
  const [selectedPaymentOption, setSelectedPaymentOption] = useState(null);
  const [amount, setAmount] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentOptionsLoaded, setPaymentOptionsLoaded] = useState(false);
  const [paymentOptionsLoadError, setPaymentOptionsLoadError] = useState(null);
  const [userCards, setUserCards] = useState([]);
  const [userDigitalWallets, setUserDigitalWallets] = useState([]);
  const [feeBreakdownHash, setFeeBreakdownHash] = useState("");
  const [feeBreakdown, setFeeBreakdown] = useState([]);

  useEffect(() => {
    document.body.classList.toggle("loading-overlay-open", loading);

    return () => {
      document.body.classList.remove("loading-overlay-open");
    };
  }, [loading]);

  const fetchPaymentOptions = async (ct) => {
    try {
      const data = await sdk.getPaymentOptionsAsync(
        PAYMENT_OPTION_INTENT.Withdraw,
        ct
      );
      const options = Array.isArray(data) ? data : [];
      setPaymentOptions(options);
      setPaymentOptionsLoaded(true);

      if (options.length === 0) {
        const error = new Error("No withdrawal payment options were returned.");
        setSelectedPaymentOption(null);
        setPaymentOptionsLoadError(error);
        setError(error);
        return;
      }

      setPaymentOptionsLoadError(null);
      setError(null);
      setSelectedPaymentOption(options[0]);
    } catch (error) {
      console.error(error);
      setPaymentOptionsLoaded(true);
      setPaymentOptionsLoadError(error);
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserCards = async (ct) => {
    try {
      const data = await sdk.getUserCardsAsync(ct);
      setUserCards(data);
      setError(null);
    } catch (error) {
      console.error(error);
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDigitalWallets = async (ct) => {
    try {
      const data = await sdk.getUserDigitalWalletsAsync(ct);
      setUserDigitalWallets(data);
      setError(null);
    } catch (error) {
      console.error(error);
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeeBreakdown = useCallback(
    async (ct) => {
      try {
        if (!amount || amount <= 0) {
          setFeeBreakdownHash("");
          setFeeBreakdown([]);
          setError(null);
          return;
        }

        const response = await sdk.getWithdrawalFeeBreakdownAsync(
          { amount },
          ct
        );

        setFeeBreakdownHash(response.feeBreakdownHash);
        setFeeBreakdown(response.feeBreakdown);

        setError(null);
      } catch (error) {
        console.error(error);
        setError(error);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [amount]
  );

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const sessionId = queryParams.get("sessionId");
    if (typeof sessionId !== "string" || sessionId.length === 0) {
      setPaymentOptionsLoaded(true);
      setError(new Error("Session ID is not provided"));
      return;
    }

    const abortController = new AbortController();
    setLoading(true);
    fetchPaymentOptions(abortController.signal);
    fetchUserCards(abortController.signal);
    fetchUserDigitalWallets(abortController.signal);
    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (paymentOptionsLoadError) {
      return undefined;
    }

    const abortController = new AbortController();

    const timeoutId = setTimeout(() => {
      setLoading(true);
      fetchFeeBreakdown(abortController.signal);
    }, 1000);

    return () => {
      clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [fetchFeeBreakdown, paymentOptionsLoadError]);

  const tryExecuteWithdrawal = async (data) => {
    startPaymentStopwatch();
    setLoading(true);

    try {
      data = { ...data, amount, feeBreakdownHash };

      const request = {
        paymentOption: selectedPaymentOption,
        data
      };

      const result = await sdk.withdrawAsync(request);

      const { status, requiredAction, errorCode } = result;

      if (requiredAction && requiredAction.action) {
        finishPaymentAttempt({
          title: "Unsupported required action",
          status: "Action Required",
          message: "Required action is not supported: " + requiredAction.action,
          details: [["Required action", requiredAction.action]]
        });

        return result;
      }

      const withdrawalResultMessages = {
        Initiated: "Your withdrawal will be proceeded later.",
        Succeeded: "Your withdrawal succeeded.",
        Failed: errorCode
          ? `Your withdrawal failed: ${errorCode}`
          : "Your withdrawal failed.",
        RequiresManualApproval:
          "Your withdrawal will be processed after manual approval."
      };

      finishPaymentAttempt({
        title: "Withdrawal result",
        status: status || "Unknown",
        message:
          withdrawalResultMessages[status] ||
          `Withdrawal status is not supported: ${status}`,
        details: [errorCode ? ["Error code", errorCode] : null].filter(Boolean)
      });

      setError(null);
    } catch (error) {
      console.error(error);

      const errorMessage =
        formatDepositHistoryMessage(error.message) ||
        formatDepositHistoryMessage(error);

      finishPaymentAttempt({
        title: "Withdrawal error",
        status: "Crashed",
        message: errorMessage
          ? `Withdrawal crashed: ${errorMessage}`
          : "Withdrawal crashed.",
        details: [["Error message", errorMessage]].filter(([, value]) =>
          Boolean(value)
        )
      });

      setError(error);
    } finally {
      setLoading(false);
    }
  };

  if (!paymentOptionsLoaded || paymentOptionsLoadError) {
    return (
      <div className="App">
        {loading && !paymentOptionsLoadError && (
          <div
            className="loading"
            role="status"
            aria-live="polite"
            aria-label="Loading"
          >
            <span className="loading-spinner" aria-hidden="true"></span>
            <span className="loading-text">Loading</span>
          </div>
        )}
        {paymentOptionsLoadError && (
          <div className="error">
            An error occurred: {paymentOptionsLoadError.message}
            <p>{paymentOptionsLoadError.stack}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="App">
      {loading && (
        <div
          className="loading"
          role="status"
          aria-live="polite"
          aria-label="Loading"
        >
          <span className="loading-spinner" aria-hidden="true"></span>
          <span className="loading-text">Loading</span>
        </div>
      )}

      <PaymentResultModal result={paymentResult} onClose={closePaymentResult} />

      {error && (
        <div className="error">
          An error occurred: {error.message}
          <p>{error.stack}</p>
        </div>
      )}

      <h1>Payment Options</h1>

      <div className="payment-options">
        {paymentOptions.map((paymentOption) => (
          <div
            className={getPaymentOptionClassName(paymentOption)}
            key={paymentOption.id}
          >
            <input
              id={paymentOption.id}
              type="radio"
              name="paymentOption"
              value={paymentOption.id}
              checked={paymentOption.id === selectedPaymentOption?.id}
              onChange={() => setSelectedPaymentOption(paymentOption)}
            />
            <label htmlFor={paymentOption.id}>
              {paymentOption.displayName}
            </label>
          </div>
        ))}
      </div>

      <div className="separator"></div>

      <div className="selected-payment-option">
        Selected Payment Option: {selectedPaymentOption?.displayName || "None"}
        {selectedPaymentOption?.minAmount && (
          <div className="min-amount">
            Min Amount: {selectedPaymentOption.minAmount} {sdk.currency}
          </div>
        )}
        {selectedPaymentOption?.maxAmount && (
          <div className="max-amount">
            Max Amount: {selectedPaymentOption.maxAmount} {sdk.currency}
          </div>
        )}
      </div>

      <div className="separator"></div>
      <div className="amount form-group">
        <label htmlFor="amount">Amount:</label>
        <input
          type="number"
          id="amount"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />

        <div className="fee-breakdown">
          {feeBreakdown.map((fee, index) => (
            <div key={index} className="fee-breakdown-item">
              {fee.ruleName}: {fee.calculatedFee}
            </div>
          ))}
        </div>
      </div>

      {selectedPaymentOption && (
        <div className="form">
          {/* eslint-disable-next-line no-undef */}
          {selectedPaymentOption.dataType === PGDataType.CreditCard && (
            <WithdrawalCreditCardForm
              onSubmit={tryExecuteWithdrawal}
              userCards={userCards}
              selectedPaymentOption={selectedPaymentOption}
            />
          )}
          {/* eslint-disable-next-line no-undef */}
          {selectedPaymentOption.dataType === PGDataType.Amount && (
            <AmountForm onSubmit={tryExecuteWithdrawal} />
          )}
          {/* eslint-disable-next-line no-undef */}
          {selectedPaymentOption.dataType === PGDataType.Email && (
            <ContactForm
              field="email"
              label="Email"
              inputType="email"
              autoComplete="email"
              placeholder="Enter payout email"
              onSubmit={tryExecuteWithdrawal}
            />
          )}
          {/* eslint-disable-next-line no-undef */}
          {selectedPaymentOption.dataType === PGDataType.PhoneNumber && (
            <ContactForm
              field="phoneNumber"
              label="Phone Number"
              inputType="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="0755111222"
              hint="With or without the country code, e.g. 0755111222 or +40755111222."
              onSubmit={tryExecuteWithdrawal}
            />
          )}
          {
            // eslint-disable-next-line no-undef
            (selectedPaymentOption.dataType === PGDataType.ApplePay ||
              // eslint-disable-next-line no-undef
              selectedPaymentOption.dataType === PGDataType.GooglePay) && (
              <WithdrawalDigitalWalletForm
                onSubmit={tryExecuteWithdrawal}
                userDigitalWallets={userDigitalWallets}
                selectedPaymentOption={selectedPaymentOption}
              />
            )
          }
        </div>
      )}
    </div>
  );
}

export default Withdrawal;
